import React, { FC, useState, useEffect, ChangeEvent, memo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import HelpIcon from '@mui/icons-material/Help';
import { Box, Button, Typography, Stack, Tooltip as MuiTooltip, IconButton } from '@mui/material';

import { ImageEditor as ImageEditorComponent } from '@shared/components/ImageEditor';
import type { FileEventTarget } from '@shared/types';
import { readFile, shouldPreserveTransparency, getImageTypeFromUrl } from '@shared/utils/file-utils';

import CropperOverlay from './CropperOverlay';
import type { MobileCropperProps } from './mobile-cropper.types';

import styles from '../image-cropper.module.scss';

const MAX_SIDE = 50;

// Вспомогательная функция для уменьшения изображения
const resizeImage = (imageSrc: string, maxWidth: number): Promise<string> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      const scaleFactor = maxWidth / img.width;
      const canvas = document.createElement('canvas');
      canvas.width = maxWidth;
      canvas.height = img.height * scaleFactor;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', 0.8)); // Сохранение с качеством 80%
    };
  });
};

const MobileCropper: FC<MobileCropperProps> = ({
  onChange,
  value,
  maskImage,
  aspectRatio,
  isDisabled = false,
  label,
  toolTipContent,
  containerClassName = '',
  fieldName,
  onCroppedPixelsChange,
  hasError = false,
  errorMessage,
  gameType,
  showImageEditor = false,
  onImageEdited,
}) => {
  const { t } = useTranslation();

  const [errorImage, setErrorImage] = useState(false);
  const [open, setOpen] = useState(false);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [file, setFile] = useState<File>();
  const [isDragOver, setIsDragOver] = useState(false);
  const [isPasteActive, setIsPasteActive] = useState(false);
  const [hasTransparency, setHasTransparency] = useState(false);
  const [isImageEditorOpen, setIsImageEditorOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Функция для определения прозрачности по типу файла
  const checkImageTransparency = async (file: File | string) => {
    let shouldPreserve = shouldPreserveTransparency(file);
    
    // Для Flappy Bird всегда сохраняем прозрачность
    if (gameType === 'flappy') {
      shouldPreserve = true;
      console.log('Flappy Bird detected - forcing transparency preservation');
    } else {
      // Если это URL без явного расширения, попробуем определить тип через fetch
      if (typeof file === 'string' && !shouldPreserve && (file.startsWith('http') || file.startsWith('/'))) {
        try {
          const contentType = await getImageTypeFromUrl(file);
          if (contentType) {
            shouldPreserve = contentType.includes('png') || contentType.includes('webp');
          }
        } catch (error) {
          console.log('Could not determine content type from URL, using fallback');
        }
      }
    }
    
    // console.log('Should preserve transparency:', shouldPreserve, 'for file:', file, 'gameType:', gameType);
    setHasTransparency(shouldPreserve);
  };

  useEffect(() => {
    if (!value) {
      setImageSrc('');
      return;
    }

    if (typeof value === 'string') {
      setImageSrc(value);
      checkImageTransparency(value);
    } else {
      setFile(value);
      readFile(value).then((res) => {
        setImageSrc(res);
        onChange(value);
        checkImageTransparency(value);
      }).catch((error) => {
        console.error('Ошибка чтения файла:', error);
      });
    }
  }, [value]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const target = e.target as FileEventTarget;

    if (target.files && target.files.length > 0) {
      const temp_file = target.files[0];
      
      readFile(temp_file)
        .then((res) => {
          setImageSrc(res);
          setFile(temp_file);
          onChange(temp_file);
          onCroppedPixelsChange(fieldName, null);
          checkImageTransparency(temp_file);
        })
        .catch((e) => {
          setErrorImage(true);
          setImageSrc(imageSrc);
        });
    } else {
      setErrorImage(true);
    }
  };

  const handleConfirmClick = (croppedImage: File | string | null) => {
    if (!croppedImage) {
      return;
    }

    if (typeof croppedImage === 'string') {
      setImageSrc(croppedImage);
      onChange(croppedImage);
      onCroppedPixelsChange(fieldName, null);
    } else {
      setFile(croppedImage);
      readFile(croppedImage).then((file) => {
        setImageSrc(file);
        onChange(croppedImage);
        onCroppedPixelsChange(fieldName, null);
      }).catch(console.error);
    }
  };

  const getFileName = () => {
    return t('clientAll.uploadImage');
  };

  const handleClearClink = () => {
    setImageSrc('');
    setFile(undefined);
    onCroppedPixelsChange(fieldName, null);
    onChange('');
  };

  const handleImageEdited = (editedImage: File) => {
    setFile(editedImage);
    readFile(editedImage)
      .then((res) => {
        setImageSrc(res);
        onChange(editedImage);
      })
      .catch((error) => {
        console.error('Ошибка загрузки отредактированного изображения:', error);
      });
    
    setIsImageEditorOpen(false);
    
    if (onImageEdited) {
      onImageEdited(editedImage);
    }
  };

  // Функция для обработки вставки из буфера обмена
  const handlePaste = async (e: ClipboardEvent) => {
    if (isDisabled) return;
    
    const items = e.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      if (item.type.startsWith('image/')) {
        const file = item.getAsFile();
        
        if (file && file.size > 0) {
          try {
            const res = await readFile(file);
            setImageSrc(res);
            setFile(file);
            onChange(file);
            onCroppedPixelsChange(fieldName, null);
            setErrorImage(false);
            return;
          } catch (error) {
            setErrorImage(true);
          }
        }
      }
    }
    
    // Если не нашли изображения в clipboardData, пробуем Clipboard API
    if (/Android|iPhone|iPad|iPod/.test(navigator.userAgent)) {
      try {
        const clipboardItems = await navigator.clipboard.read();
        
        for (const item of clipboardItems) {
          for (const type of item.types) {
            if (type.startsWith('image/')) {
              try {
                const blob = await item.getType(type);
                
                if (blob && blob.size > 0) {
                  const newFile = new File([blob], `pasted-image.${type.split('/')[1]}`, { type });
                  
                  const res = await readFile(newFile);
                  setImageSrc(res);
                  setFile(newFile);
                  onChange(newFile);
                  onCroppedPixelsChange(fieldName, null);
                  setErrorImage(false);
                  return;
                }
              } catch (blobError) {
                continue;
              }
            }
          }
        }
      } catch (mobileError) {
        // console.log('Mobile paste error:', mobileError);
      }
    }
  };

  // Функция для принудительной активации вставки (для мобильных устройств)
  const handleForcePaste = async () => {
    if (isDisabled) return;
    
    try {
      // Сначала пробуем современный Clipboard API
      if (navigator.clipboard && navigator.clipboard.read) {
        try {
          const data = await navigator.clipboard.read();
          
          for (const item of data) {
            // Проверяем все возможные типы изображений
            const imageTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/gif'];
            
            for (const type of imageTypes) {
              if (item.types.includes(type)) {
                try {
                  const file = await item.getType(type);
                  if (file) {
                    const blob = new Blob([file], { type: file.type });
                    const newFile = new File([blob], `pasted-image.${type.split('/')[1]}`, { type: file.type });
                    
                    const res = await readFile(newFile);
                    setImageSrc(res);
                    setFile(newFile);
                    onChange(newFile);
                    onCroppedPixelsChange(fieldName, null);
                    setErrorImage(false);
                    return;
                  }
                } catch (typeError) {
                  continue;
                }
              }
            }
          }
        } catch (clipboardError) {
          // console.log('Clipboard API error:', clipboardError);
        }
      }
      
      // Альтернативный способ для мобильных устройств - используем старый API
      if (/Android|iPhone|iPad|iPod/.test(navigator.userAgent)) {
        try {
          const clipboardItems = await navigator.clipboard.read();
          
          for (const item of clipboardItems) {
            // Проверяем все типы данных
            for (const type of item.types) {
              if (type.startsWith('image/')) {
                try {
                  const blob = await item.getType(type);
                  
                  if (blob && blob.size > 0) {
                    const newFile = new File([blob], `pasted-image.${type.split('/')[1]}`, { type });
                    
                    const res = await readFile(newFile);
                    setImageSrc(res);
                    setFile(newFile);
                    onChange(newFile);
                    onCroppedPixelsChange(fieldName, null);
                    setErrorImage(false);
                    return;
                  }
                } catch (blobError) {
                  continue;
                }
              }
            }
          }
        } catch (mobileError) {
          // console.log('Mobile clipboard error:', mobileError);
        }
      }
        
      // Если Clipboard API недоступен или не работает, показываем инструкции
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        alert(t('client.iosImagePasteInstructions'));
      } else if (/Android/.test(navigator.userAgent)) {
        alert(t('client.androidImagePasteInstructions'));
      } else {
        alert(t('client.pasteImageError'));
      }
    } catch (error) {
      // Специальные подсказки для разных устройств
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        alert(t('client.iosImagePasteInstructions'));
      } else if (/Android/.test(navigator.userAgent)) {
        alert(t('client.androidImagePasteInstructions'));
      } else {
        alert(t('client.pasteImageError'));
      }
    }
  };

  // Функция для обработки перетаскивания файлов - только для десктопа
  const handleDragOver = (e: React.DragEvent) => {
    if (isDisabled || /Android|iPhone|iPad|iPod/.test(navigator.userAgent)) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (isDisabled || /Android|iPhone|iPad|iPod/.test(navigator.userAgent)) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    if (isDisabled || /Android|iPhone|iPad|iPod/.test(navigator.userAgent)) return;
    e.preventDefault();
    setIsDragOver(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const droppedFile = files[0];
      if (droppedFile.type.startsWith('image/')) {
        try {
          const res = await readFile(droppedFile);
          setImageSrc(res);
          setFile(droppedFile);
          onChange(droppedFile);
          onCroppedPixelsChange(fieldName, null);
          setErrorImage(false);
        } catch (error) {
          setErrorImage(true);
        }
      }
    }
  };

  // Обработчик клика для активации области вставки
  const handleClick = () => {
    if (!isDisabled && !imageSrc) {
      // Устанавливаем фокус на контейнер для активации вставки
      containerRef.current?.focus();
      setIsPasteActive(true);
      setTimeout(() => setIsPasteActive(false), 300);
    }
  };

  // Добавляем обработчики событий
  useEffect(() => {
    const handleGlobalPaste = (e: ClipboardEvent) => {
      // Проверяем, что фокус находится только в нашем контейнере
      if (containerRef.current?.contains(document.activeElement)) {
        handlePaste(e);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v' && !isDisabled) {
        setIsPasteActive(true);
        setTimeout(() => setIsPasteActive(false), 200);
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'v') {
        setIsPasteActive(false);
      }
    };

    document.addEventListener('paste', handleGlobalPaste);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);
    
    return () => {
      document.removeEventListener('paste', handleGlobalPaste);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDisabled]);

  return (
    <div 
      ref={containerRef}
      className={`${styles.imageCropperWrapper} ${containerClassName} ${hasError ? styles.error : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
      tabIndex={!isDisabled && !imageSrc ? 0 : -1}
      style={{
        position: 'relative',
        border: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) 
          ? 'none' 
          : isDragOver 
            ? '2px dashed #1976d2' 
            : isPasteActive 
              ? '2px dashed #ff9800' 
              : '2px dashed #4caf50',
        backgroundColor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent)
          ? 'transparent'
          : isDragOver 
            ? 'rgba(25, 118, 210, 0.1)' 
            : isPasteActive 
              ? 'rgba(255, 152, 0, 0.1)' 
              : 'rgba(76, 175, 80, 0.05)',
        transition: 'all 0.2s ease',
        cursor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'default' : (isDragOver || isPasteActive ? 'pointer' : 'pointer')
      }}
    >
      {errorImage && (
        <Box className={styles.errorModalWrapper}>
          <Box component="div" className={styles.errorModal}>
            <Typography variant="h6" noWrap className="cropperLabel">
              Image should be less than 5.000 px
            </Typography>
            <Box className={styles.errorModalBtnWrapper}>
              <Button
                color="error"
                size="small"
                className={styles.errorModalBtn}
                onClick={() => setErrorImage(false)}
              >
                Close
              </Button>
            </Box>
          </Box>
        </Box>
      )}
      <Stack className={styles.imageCropperHeader} spacing={1} sx={{ mt: 1 }}>
        <Stack
          className={`${styles.imageCropperHeader} tooltipWrapperInline`}
          direction="row"
        >
          <Typography 
            variant="h6" 
            className="cropperLabel"
            sx={{
              fontSize: {
                xs: '0.75rem', // 12px на мобильных
                sm: '0.875rem', // 14px на планшетах
                md: '1rem' // 16px на десктопе
              },
              fontWeight: {
                xs: 500,
                sm: 500,
                md: 500
              },
              textAlign: 'center',
              maxWidth: '100%',
              lineHeight: 1.2,
              wordBreak: 'break-word',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: 'normal'
            }}
          >
            {label}
          </Typography>
          {!!toolTipContent && (
            <MuiTooltip title={toolTipContent} enterTouchDelay={0} leaveTouchDelay={3000}>
              <IconButton 
                size="small" 
                aria-label="help"
                sx={{ 
                  padding: '1px',
                  minWidth: 'auto',
                  '& .MuiButton-startIcon': {
                    margin: 0
                  }
                }}
              >
                <HelpIcon sx={{ fontSize: '0.75rem' }} />
              </IconButton>
            </MuiTooltip>
          )}
        </Stack>

        {imageSrc && (
          <Box 
            component="div" 
            className={styles.cropArea}
            sx={{
              border: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'none' : undefined,
              backgroundColor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'transparent' : undefined,
              position: 'relative',
              minHeight: { xs: '150px', sm: '200px' },
              maxHeight: { xs: '300px', sm: '400px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              padding: { xs: '0.5em', sm: '1em' },
              transition: 'all 0.3s ease-in-out',
              overflow: 'hidden', // Предотвращаем выход изображений за границы
              '&:hover': {
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <Box
              className={styles.imagePreview}
              component="img"
              src={imageSrc}
              sx={{
                backgroundColor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'transparent' : undefined,
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                borderRadius: '12px',
                objectFit: 'contain',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                transition: 'transform 0.2s ease-in-out',
                overflow: 'hidden', // Предотвращаем выход изображений за границы
                '&:hover': {
                  transform: 'scale(1.02)'
                }
              }}
            />
          </Box>
        )}

        {/* Пустое состояние с улучшенными стилями */}
        {!imageSrc && (
          <Box 
            component="div" 
            className={styles.cropArea}
            sx={{
              border: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'none' : '2px dashed rgba(0, 0, 0, 0.1)',
              backgroundColor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'transparent' : 'rgba(236, 236, 236, 0.5)',
              position: 'relative',
              minHeight: { xs: '150px', sm: '200px' },
              maxHeight: { xs: '300px', sm: '400px' },
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px',
              padding: { xs: '0.5em', sm: '1em' },
              transition: 'all 0.3s ease-in-out',
              overflow: 'hidden', // Предотвращаем выход содержимого за границы
              '&:hover': {
                backgroundColor: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'transparent' : 'rgba(236, 236, 236, 0.7)',
                border: /Android|iPhone|iPad|iPod/.test(navigator.userAgent) ? 'none' : '2px dashed rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            <Typography
              variant="body2"
              color="textSecondary"
              sx={{
                textAlign: 'center',
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                fontStyle: 'italic',
                opacity: 0.7
              }}
            >
              {t('clientAll.uploadImage') || 'Image'}
            </Typography>
          </Box>
        )}

        <Stack 
          spacing={1} 
          className={styles.actionButtons}
          sx={{
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            mt: 1
          }}
        >
          <Button
            variant="contained"
            size="small"
            disabled={isDisabled}
            component="label"
            className="uploadBtn"
            sx={{
              minWidth: '250px',
              width: '20em',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
              }
            }}
          >
            <span className="uploadBtnText">{getFileName()}</span>
            <input
              accept="image/*"
              type={'file'}
              hidden
              onChange={handleFileChange}
            />
          </Button>
          
          {/* Подсказка для вставки и перетаскивания - только для десктопа */}
          {!imageSrc && !isDisabled && !(/Android|iPhone|iPad|iPod/.test(navigator.userAgent)) && (
            <Typography 
              variant="caption" 
              color="textSecondary" 
              sx={{ 
                textAlign: 'center', 
                fontSize: '0.75rem',
                fontStyle: 'italic'
              }}
            >
              {t('client.dragDropOrPaste') || 'Перетащите изображение или вставьте из буфера обмена (Ctrl+V)'}
            </Typography>
          )}

          {imageSrc && (
            <Button
              size="small"
              disabled={isDisabled}
              variant="outlined"
              color="success"
              onClick={handleOpen}
            >
              {t('client.zoomCrop')}
            </Button>
          )}
          {imageSrc && showImageEditor && (
            <Button
              size="small"
              disabled={isDisabled}
              variant="outlined"
              color="secondary"
              onClick={() => {
                console.log('=== ОТКРЫТИЕ РЕДАКТОРА ===');
                console.log('imageSrc:', imageSrc);
                console.log('file:', file);
                console.log('imageSrc length:', imageSrc.length);
                console.log('imageSrc starts with data:', imageSrc.startsWith('data:'));
                setIsImageEditorOpen(true);
              }}
            >
              {t('imageEditor.edit') || 'Редактировать'}
            </Button>
          )}
          {imageSrc && (
            <Button
              size="small"
              disabled={isDisabled}
              color="error"
              onClick={handleClearClink}
            >
               {t('client.clear')}
            </Button>
          )}
        </Stack>
      </Stack>

      {hasError && errorMessage && (
        <Typography variant="caption" className={styles.errorMessage}>
          {errorMessage}
        </Typography>
      )}

      <CropperOverlay
        key={fieldName + open}
        onConfirm={handleConfirmClick}
        onClose={handleClose}
        isOpen={open}
        imageSrc={imageSrc}
        file={file}
        maskImage={maskImage}
        aspectRatio={aspectRatio}
        fileName={getFileName()}
        fieldName={fieldName}
        onCroppedPixelsChange={(fName, croppedPixels) => {
          onCroppedPixelsChange(fName, croppedPixels);
        }}
        gameType={gameType}
        hasTransparency={hasTransparency}
      />
      
      {/* Редактор изображений */}
      {isImageEditorOpen && (
        <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, bgcolor: 'white', overflow: 'hidden' }}>
          <ImageEditorComponent
            initialImage={imageSrc}
            onSave={(dataUrl) => {
              // Конвертируем dataUrl в File
              fetch(dataUrl)
                .then(res => res.blob())
                .then(blob => {
                  const file = new File([blob], 'edited-image.png', { type: 'image/png' });
                  handleImageEdited(file);
                });
            }}
          />
        </Box>
      )}
      {isImageEditorOpen && (
        <div style={{ display: 'none' }}>
          Debug: imageSrc = {imageSrc ? 'загружено' : 'не загружено'}
        </div>
      )}
    </div>
  );
};

export default memo(MobileCropper);
